import { ENCRYPT } from '@/constants';
import { StockProps } from '@/hooks/StockContext';
import { CartDetailsProps, CartItems } from '@/hooks/CartContext';
import { CardDescription, ClientProps, CustomerDataProps, DataSetsProps, DiscountProps, LogEvents } from '@/types';
import type { InconsistenciesDataState } from './inconsistenciesData';

export const storageKey = '@PWA_RCA';

export const tableKeys = [
  'rca',
  'temporary_token_aws',
  'datasets',
  'datasets_imagens',
  'has_new_datasets',
  'new_data_sets',
  'token_aws',
  'discounts',
  'rca_usuario',
  'user_logado',
  'token_magento',
  'token_offline',
  'selected_client',
  'cart_id',
  'cart_details',
  'cod_pessoa',
  'cart',
  'isExactSearch',
  'client_cart',
  'is_first_load',
  'offline_orders',
  'login_date',
  'login',
  'network_status',
  'last_route',
  'sent_events',
  'cardDescription',
  'stock_left',
  'synched_orders',
  'lastSync',
  'paymentCode',
  'cardMode',
  'lastCheckedVersion',
  'new_client_store_images',
  'new_client_contrato_social',
  'new_client_invoices',
  'user_login_data',
  'selected_rca',
  'order_numbers',
  'cart_opened_at',
  'scroll_memory_v1',
  'pending_selected_rca',
  'cart_sort',
  'last_data_sync',
  'alerta_carrinhos_abandonados',
  'last_order_send_time',
  'recados',
  'selected_brand',
  'system_version',
  'uuid',
  'inconsistencies_data',
  'session_watchdog_v1',
] as const;

export type Tables = (typeof tableKeys)[number];

type TableTypes = {
  rca: CustomerDataProps;
  token_aws: String;
  cardMode: 'list' | 'grid';
  selected_brand: string | { codLoja: number; codSegmento: number; brandKey?: string; brandName?: string };
  cardDescription: CardDescription;
  rca_usuario: CustomerDataProps;
  user_logado: CustomerDataProps;

  uuid: string;
  session_watchdog_v1: {
    sessionId: string;
    startedAt: number;
    lastSeenAt: number;
    running: boolean;
    cleanExitAt?: number;
    lastNavType?: string;
  };
  token_magento: String;
  token_offline: String;
  system_version: String;
  discounts: DiscountProps;
  datasets: DataSetsProps[];
  datasets_imagens: DataSetsProps[];
  new_data_sets: DataSetsProps[];
  selected_client: ClientProps;
  selected_rca: CustomerDataProps;
  cart_details: Record<string, CartDetailsProps>;
  paymentCode: Record<number, number>;
  cart_id: string;
  sent_events: Record<string, boolean>;
  is_first_load: string;
  cart: Array<CartItems>;
  client_cart: Array<CartItems>;
  offline_orders: any;
  login_date: Date;
  last_route: string;
  inconsistencies_data: InconsistenciesDataState | boolean;
  alerta_carrinhos_abandonados: number;
  temporary_token_aws: string;
  cod_pessoa: string;
  network_status: 'offline' | 'online';
  synched_orders: any;
  stock_left: Record<number, Record<number, StockProps>>;
  lastSync: string;
  lastCheckedVersion: string;
  new_client_store_images: any;
  new_client_contrato_social: any;
  new_client_invoices: any;
  login: string;
  user_login_data: string;
  order_numbers: any;
  cart_opened_at: string;
  isExactSearch: boolean;
  has_new_datasets: boolean;
  scroll_memory_v1: Record<string, number>;
  recados: Record<number, string>;

  pending_selected_rca: CustomerDataProps;
  cart_sort: 'inclusao' | 'alfabetica';
  last_data_sync: string;
  last_order_send_time: number;
};

const toCoded = (str: string) => {
  if (!str || !ENCRYPT) return str;

  try {
    const encoder = new TextEncoder();
    const uint8Array = encoder.encode(str);
    const binaryStr = String.fromCharCode(...uint8Array);
    return window.btoa(binaryStr);
  } catch (error) {
    return str;
  }
};

const fromCoded = (str: string) => {
  if (!str || !ENCRYPT) return str;

  try {
    const binaryStr = window.atob(str);
    const uint8Array = new Uint8Array([...binaryStr].map((char) => char.charCodeAt(0)));
    const decoder = new TextDecoder();
    return decoder.decode(uint8Array);
  } catch (e) {
    return str;
  }
};

const toJSONOrString = (input: any) => {
  try {
    return toCoded(JSON.stringify(input));
  } catch (error) {
    return toCoded(input);
  }
};

const parseJSONOrString = (input: any) => {
  try {
    return JSON.parse(fromCoded(input));
  } catch (e) {
    return fromCoded(input);
  }
};

const mountKey = (name: string, id?: string) => toCoded(`${storageKey}:${name}${id ? ':' + id : ''}`);

/** Chave bruta no `localStorage` para um item tipado (útil para backup seletivo). */
export const getLocalStorageKeyForTable = (name: Tables, id?: string) => mountKey(name, id);

export const getOnStorage = <T extends Tables>(name: T, id?: string): TableTypes[T] | null => {
  const storageItem = localStorage.getItem(mountKey(name, id));
  return parseJSONOrString(storageItem) as TableTypes[T] | null;
};

/**
 * Suspect #6 from orders-issues.md: prior implementation `alert`-ed on
 * QuotaExceededError then silently returned. Callers (notably
 * `addOrderToStorage`) did not check the return value, so checkout would
 * report success even though nothing was persisted.
 *
 * New contract: returns `true` on success, `false` on QuotaExceededError.
 * Callers that care about durability MUST check the return value (the new
 * `enqueueOrder` in useCheckoutPage does). The quota failure is also
 * surfaced to telemetry so we can see how often this happens in production.
 */
export const setOnStorage = (name: Tables, data: any, id?: string): boolean => {
  try {
    localStorage.setItem(mountKey(name, id), toJSONOrString(data));
    return true;
  } catch (error) {
    if (error instanceof DOMException && error.name === 'QuotaExceededError') {
      console.error('[storage] QuotaExceededError on key', name, '— data NOT persisted', {
        approxSize: typeof data === 'string' ? data.length : JSON.stringify(data ?? '').length,
      });
      // User-facing message preserved; the caller decides what to do with `false`.
      try {
        alert('O armazenamento local do navegador atingiu o limite');
      } catch {
        /* alert may be unavailable in some envs */
      }
      return false;
    }
    console.error('Erro ao salvar dados no storage:', error);
    throw error;
  }
};

export const removeFromStorage = (name: Tables, id?: string) => {
  localStorage.removeItem(mountKey(name, id));
};

export const updateOnStorage = (name: Tables, data: any, id?: string) => {
  const storageItem = getOnStorage(name, id);
  if (!storageItem) {
    console.log('Item não encontrado no storage');
    return;
  }

  const updatedItem = { ...(storageItem || {}), ...(data || {}) };
  setOnStorage(name, updatedItem, id);
};

export const updateSpecificFieldInStorage = (storageKey: any, field: string, newValue: string) => {
  let currentData = getOnStorage(storageKey);

  if (currentData && currentData.hasOwnProperty(field)) {
    currentData[field] = newValue;
    updateOnStorage(storageKey, currentData);
  } else {
    console.error('Campo não encontrado no objeto');
  }
};
